import { getLogger } from "@expert/logging";
import { Autocomplete, Stack, Text } from "@mantine/core";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import type { ClipboardEvent, JSX } from "react";
import { useEffect, useState } from "react";
import IMaskInput from "react-imask/input";
import { CloseX } from "@/common-ui";
import {
    type Contact,
    type ContactGroup,
    type InputOption,
    type MaxLengthState,
    MDN_MASK,
    NO_MASK,
    PHONE_MAX_LENGTH,
    TFN_LENGTH,
    US,
    VDN_MASK,
    VDN_MAX_LENGTH,
} from "@/shared-types";
import { extractNumbers, groupContacts, tryGetByVdnName, tryGetByVdnNumber } from "@/shared-utils";
import { useContactsStateStore } from "../vdnContacts.store";
import classes from "./PhoneNumberInput.module.css";

const logger = getLogger({ module: "PhoneNumberInput" });

interface PhoneNumberInputProps {
    transferTfn: string | undefined;
    setTransferTfn: (tfn: string | undefined) => void;
    transferVdn: Contact | undefined;
    setTransferVdn: (vdn: Contact | undefined) => void;
}
export function PhoneNumberInput({
    transferTfn,
    setTransferTfn,
    transferVdn,
    setTransferVdn,
}: PhoneNumberInputProps): JSX.Element {
    const { contacts } = useContactsStateStore();

    const [displayValue, setDisplayValue] = useState<string>("");
    const [validTransferInput, setValidTransferInput] = useState<boolean>(false);

    const [quickTransferContacts, setQuickTransferContacts] = useState<ContactGroup[]>(groupContacts(contacts));

    const [enforceMaxLength, setEnforceMaxLength] = useState<MaxLengthState>({ enforce: true, length: TFN_LENGTH });

    // Switch that alternates between all 3 inputs that require different masks
    const [inputMask, setInputMask] = useState<string>(NO_MASK);
    const [inputType, setInputType] = useState<InputOption>();

    useEffect(() => {
        try {
            setQuickTransferContacts(groupContacts(contacts));
        } catch (error: unknown) {
            logger.error({ error }, "Error setting contacts:");
        }
    }, [contacts]);

    useEffect(() => {
        switch (inputType) {
            case "PhoneNumber":
                setInputMask(MDN_MASK);
                setEnforceMaxLength({ enforce: true, length: PHONE_MAX_LENGTH });
                break;
            case "Extension":
                setInputMask(VDN_MASK);
                setEnforceMaxLength({ enforce: true, length: VDN_MAX_LENGTH });
                break;
            default:
                setInputMask(NO_MASK);
                setEnforceMaxLength({ enforce: false });
                break;
        }
    }, [inputType]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (!validTransferInput) {
            // Make sure vdn & tfn are null until a valid entry
            if (transferVdn) {
                setTransferVdn(undefined);
            }
            if (transferTfn) {
                setTransferTfn(undefined);
            }
        }
    }, [validTransferInput, displayValue]);

    function isTfn(value: string): boolean {
        const parsed = parsePhoneNumberFromString(`${US}${value}`);
        return parsed?.isValid() ?? false;
    }

    function isVdnNumber(value: string): boolean {
        return !!tryGetByVdnNumber(value, contacts);
    }

    function isVdnName(value: string): boolean {
        return !!tryGetByVdnName(value, contacts);
    }

    function handleChange(value: string): void {
        if (!value) {
            setValidTransferInput(false);
            setDisplayValue(value);
            return;
        }

        setInputType("None");

        // Check for any non-phone number characters
        const isNotPhoneNumber = /[^0-9()+\- .]/.test(value);

        // Handle user entering a name (VDN)
        if (isNotPhoneNumber) {
            if (isVdnName(value)) {
                setValidTransferInput(true);
                setTransferVdn(tryGetByVdnName(value, contacts) ?? undefined);
            } else {
                setValidTransferInput(false);
            }
            setDisplayValue(value);
            return;
        }

        const digitsOnly = extractNumbers(value);

        if (isVdnNumber(digitsOnly)) {
            // Handle user entering a 7-digit number (VDN)
            setValidTransferInput(true);
            setTransferVdn(tryGetByVdnNumber(digitsOnly, contacts) ?? undefined);
            setInputType("Extension");
        } else if (isTfn(digitsOnly)) {
            // Handle user entering a 10-digit number (TFN)
            setValidTransferInput(true);
            setTransferTfn(digitsOnly);
            setInputType("PhoneNumber");
        } else {
            setValidTransferInput(false);
        }
        setDisplayValue(digitsOnly);
    }
    function handleSubmit(_: string): void {
        setDisplayValue("");
        setValidTransferInput(false);
    }

    function handlePaste(event: ClipboardEvent<HTMLInputElement>): void {
        event.preventDefault();

        const pastedData: string = event.clipboardData.getData("text");

        handleChange(pastedData);
    }

    const clearInput = () => handleChange("");

    return (
        <Stack gap="xs">
            <Text size="xs" fw="bold">
                Enter a phone number or extension
            </Text>
            <Autocomplete
                renderRoot={({ ref, ...props }) => (
                    <IMaskInput
                        mask={[
                            {
                                mask: inputMask,
                            },
                            {
                                mask: /^.*$/,
                            },
                        ]}
                        inputRef={ref as React.Ref<HTMLInputElement>}
                        maxLength={enforceMaxLength.length}
                        {...props}
                    />
                )}
                value={displayValue}
                data={quickTransferContacts}
                type="tel"
                className="fs-mask"
                classNames={{
                    input: classes.autocompleteInput,
                    option: classes.autocompleteOption,
                    section: classes.autocompleteSection,
                }}
                size="sm"
                radius="xs"
                maw="30rem"
                mb="xs"
                autoFocus
                onChange={handleChange}
                onOptionSubmit={handleSubmit}
                onPaste={handlePaste}
                rightSection={displayValue.length ? <CloseX strokeWidth={"0.1"} data-testid="clear-input" /> : null}
                rightSectionPointerEvents="auto"
                rightSectionProps={{ onClick: clearInput, title: "Clear", className: classes.clearButton }}
                data-valid-transfer-input={validTransferInput}
                data-testid="autocomplete"
                styles={{
                    input: { borderRadius: "5px", fontSize: "var(--mantine-font-size-xs)" },
                    label: { fontSize: "var(--mantine-font-size-xs)" },
                }}
            />
        </Stack>
    );
}
